import type { CampaignFormData, StepProps } from '@/components/CreateCampaignDialog/createCampaignType'
import generalSwitchStyles from '@/components/CreateCampaignDialog/steps/GeneralInfoStep/GeneralInfoStep.module.css'
import { FormField } from '@/components/MultiStepForm/FormField/FormField'
import SelectRadix from '@/components/ui/SelectRadix'
import api from '@/services/api'
import { useBrandQuery } from '@/services/brands/queries.ts'
import { Switch } from 'radix-ui'
import { useEffect, useState } from 'react'
import styles from './AllocateFundsStep.module.css'

export const AllocateFundsStep = ({ data, updateData, errors }: StepProps) => {
  const [rewardsList, setRewardsList] = useState<string[]>([])
  const [rewardsLoadError, setRewardsLoadError] = useState(false)
  const [rewardsLoading, setRewardsLoading] = useState(true)
  const payoutEnabled = (data.rewards || []).includes('Payout')
  const { data: brand } = useBrandQuery()

  // Load rewards list on mount
  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const resp = await api.get<{ items: string[] }>('/rewards')
        if (!mounted) return
        const items = Array.isArray(resp.data?.items) ? resp.data.items : []
        setRewardsList(items)
      } catch (_e) {
        if (!mounted) return
        setRewardsLoadError(true)
      } finally {
        if (mounted) setRewardsLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (rewardsList.length > 0 && (data.rewards || []).length === 0) {
      updateData({ rewards: [rewardsList[0]] })
    }
  }, [rewardsList, data.rewards, updateData])

  const toggleReward = (reward: string, checked: boolean) => {
    const selected = new Set<string>(data.rewards || [])
    if (checked) {
      selected.add(reward)
    } else {
      if (selected.size <= 1 && selected.has(reward)) {
        return
      }
      selected.delete(reward)
    }
    updateData({ rewards: Array.from(selected) })
  }

  const updateBudget = (updates: Partial<CampaignFormData['budget']>) => {
    updateData({
      budget: {
        value: data.budget?.value ?? 0,
        currency: data.budget?.currency ?? '',
        ...updates,
      },
    })
  }

  return (
    <div className={styles.Container}>
      {payoutEnabled && (
        <div className={styles.Header}>
          <div className={styles.Item}>
            <p className={styles.Title}>Total Account Balance</p>
            {/*TODO change when will be done at the backend*/}
            <p className={styles.Value}>
              {brand?.annualBudgetCurrency} {brand?.annualBudget}
            </p>
          </div>
          <div className={styles.Separator} />
          <div className={styles.Item}>
            <p className={styles.Title}>Allocated Budget</p>
            <p className={styles.Value}>
              {data.budget?.currency} {data.budget?.value || 0}
            </p>
          </div>
        </div>
      )}

      {!rewardsLoading && !rewardsLoadError && rewardsList.length > 0 ? (
        <div>
          <p className={generalSwitchStyles.switcherLabel}>Rewards</p>
          {rewardsList.map((reward) => {
            const checked = (data.rewards || []).includes(reward)
            return (
              <div key={reward} className={`${generalSwitchStyles.SwitchAndInput} ${styles.marginY}`}>
                <Switch.Root
                  className={generalSwitchStyles.Root}
                  id={`reward-${reward}`}
                  checked={checked}
                  onCheckedChange={(state) => toggleReward(reward, state)}
                >
                  <Switch.Thumb className={generalSwitchStyles.Thumb} />
                </Switch.Root>
                <label htmlFor={`reward-${reward}`} className={generalSwitchStyles.switcherLabel}>
                  {reward}
                </label>
              </div>
            )
          })}
        </div>
      ) : null}

      {payoutEnabled && (
        <FormField label="Currency" error={errors?.currency}>
          <div className={styles.Currency}>
            <SelectRadix
              options={['EUR', 'RON', 'USD']}
              value={data.budget?.currency || ''}
              onValueChange={(value) => updateBudget({ currency: value })}
              arialabel="Select a currency"
              placeholder="Select a currency"
              disabled={!payoutEnabled}
            />
          </div>
        </FormField>
      )}
      {payoutEnabled && (
        <div className={styles.Row}>
          <FormField label="Influencer Reward Price" error={undefined}>
            <input
              type="text"
              placeholder="Specify an amount"
              value={typeof data.payout === 'number' && Number.isFinite(data.payout) ? String(data.payout) : ''}
              onChange={(e) => {
                const raw = e.target.value.trim()
                if (raw === '') {
                  updateData({ payout: undefined })
                  return
                }
                const value = Number(raw)
                if (!Number.isNaN(value) && Number.isInteger(value) && value >= 0) {
                  const capped = Math.min(value, 100000000)
                  updateData({ payout: capped })
                }
              }}
              className={styles.Input}
              disabled={!payoutEnabled}
            />
          </FormField>
          <FormField label="Allocate Funds" error={errors?.value}>
            <input
              type="text"
              placeholder="Specify an amount"
              value={data.budget?.value || ''}
              onChange={(e) => {
                const value = Number(e.target.value)
                if (!Number.isNaN(value) && Number.isInteger(value) && value >= 0) {
                  updateBudget({ value: Math.min(value, 100000000) })
                }
              }}
              className={styles.Input}
              disabled={!payoutEnabled}
            />
          </FormField>
        </div>
      )}
    </div>
  )
}
